import { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  ValidateTestConventionsInputSchema,
  type Config,
  type TestConventionResult,
  type TestConventionViolation,
} from '../types/index.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import { findFiles, readText } from '../utils/fs.js';
import path from 'path';

export const validateTestConventionsTool: Tool = {
  name: 'validate_test_conventions',
  description: 'Validate that tests in a SmartStack project follow conventions: naming ({Method}_When{Condition}_Should{Result}), structure (Tests/Unit, Tests/Integration), patterns (AAA), assertions (FluentAssertions), and mocking (Moq).',
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Project path to validate (default: configured SmartStack path)',
      },
      checks: {
        type: 'array',
        items: {
          type: 'string',
          enum: ['naming', 'structure', 'patterns', 'assertions', 'mocking', 'all'],
        },
        default: ['all'],
        description: 'Types of convention checks to perform',
      },
      autoFix: {
        type: 'boolean',
        default: false,
        description: 'Automatically fix minor issues (naming only)',
      },
    },
  },
};

// Convention patterns
const PATTERNS = {
  // Test method naming: MethodName_WhenCondition_ShouldExpectedResult
  testMethodNaming: /^(\w+)_When(\w+)_Should(\w+)$/,
  // Alternative valid patterns
  testMethodNamingAlt: /^(\w+)_Should(\w+)_When(\w+)$/,
  // Fact/Theory attributes
  factAttribute: /\[Fact\]/,
  theoryAttribute: /\[Theory\]/,
  // AAA pattern comments
  arrangeComment: /\/\/\s*Arrange/i,
  actComment: /\/\/\s*Act/i,
  assertComment: /\/\/\s*Assert/i,
  // FluentAssertions
  fluentAssertions: /\.Should\(\)/,
  // Moq
  moqUsage: /new Mock<|Mock<\w+>/,
  moqSetup: /\.Setup\(|\.Verify\(/,
  // Test class naming
  testClassNaming: /public class (\w+)Tests/,
  // xUnit patterns
  asyncTestMethod: /public async Task (\w+)/,
  syncTestMethod: /public void (\w+)/,
};

export async function handleValidateTestConventions(
  args: unknown,
  config: Config
): Promise<string> {
  const input = ValidateTestConventionsInputSchema.parse(args);
  const projectPath = input.path || config.smartstack.projectPath;
  const checks = input.checks.includes('all')
    ? ['naming', 'structure', 'patterns', 'assertions', 'mocking']
    : input.checks;

  logger.info('Validating test conventions', { projectPath, checks });

  const structure = await findSmartStackStructure(projectPath);

  const result: TestConventionResult = {
    valid: true,
    violations: [],
    suggestions: [],
    autoFixedCount: 0,
  };

  const testsPath = path.join(structure.root, 'Tests');

  try {
    // Find all test files
    let testFiles: string[] = [];
    try {
      testFiles = await findFiles('**/*Tests.cs', { cwd: testsPath });
    } catch {
      result.suggestions.push('No Tests directory found. Create a Tests project with Unit and Integration subdirectories.');
      return formatValidationResult(result);
    }

    if (testFiles.length === 0) {
      result.suggestions.push('No test files found. Create test files following the pattern {ComponentName}Tests.cs');
      return formatValidationResult(result);
    }

    // Validate structure
    if (checks.includes('structure')) {
      await validateStructure(testsPath, testFiles, result);
    }

    // Validate each test file
    for (const testFile of testFiles) {
      const fullPath = path.join(testsPath, testFile);
      const content = await readText(fullPath);

      if (checks.includes('naming')) {
        validateNaming(testFile, content, result, input.autoFix);
      }

      if (checks.includes('patterns')) {
        validatePatterns(testFile, content, result);
      }

      if (checks.includes('assertions')) {
        validateAssertions(testFile, content, result);
      }

      if (checks.includes('mocking')) {
        validateMocking(testFile, content, result);
      }
    }

    result.valid = result.violations.filter(v => v.severity === 'error').length === 0;

  } catch (error) {
    logger.error('Error validating test conventions', error);
    return `Error: ${error instanceof Error ? error.message : String(error)}`;
  }

  return formatValidationResult(result);
}

// ============================================================================
// Validation Functions
// ============================================================================

async function validateStructure(
  testsPath: string,
  testFiles: string[],
  result: TestConventionResult
): Promise<void> {
  // Check for expected directory structure
  const expectedDirs = ['Unit', 'Integration'];
  const foundDirs = new Set<string>();

  for (const file of testFiles) {
    const parts = file.split(path.sep);
    if (parts.length > 1) {
      foundDirs.add(parts[0]);
    }
  }

  for (const dir of expectedDirs) {
    if (!foundDirs.has(dir)) {
      result.violations.push({
        type: 'structure',
        severity: 'warning',
        file: testsPath,
        message: `Missing expected directory: Tests/${dir}`,
        suggestion: `Create Tests/${dir} directory for ${dir.toLowerCase()} tests`,
        autoFixable: false,
      });
    }
  }

  // Check for recommended subdirectories
  const hasUnitDomain = testFiles.some(f => f.includes(path.join('Unit', 'Domain')));
  const hasIntegrationControllers = testFiles.some(f => f.includes(path.join('Integration', 'Controllers')));

  if (!hasUnitDomain && testFiles.some(f => f.includes('Unit'))) {
    result.suggestions.push('Consider organizing unit tests into subdirectories: Unit/Domain, Unit/Services, Unit/Validators');
  }

  if (!hasIntegrationControllers && testFiles.some(f => f.includes('Integration'))) {
    result.suggestions.push('Consider organizing integration tests into subdirectories: Integration/Controllers, Integration/Repositories');
  }

  // Check for security tests
  const hasSecurityTests = testFiles.some(f => f.includes('Security'));
  if (!hasSecurityTests) {
    result.suggestions.push('Consider adding a Tests/Security directory for security-focused tests');
  }
}

function validateNaming(
  testFile: string,
  content: string,
  result: TestConventionResult,
  _autoFix: boolean
): void {
  // Validate test class naming
  const classMatch = content.match(/public class (\w+)/);
  if (classMatch) {
    const className = classMatch[1];
    if (!className.endsWith('Tests')) {
      result.violations.push({
        type: 'naming',
        severity: 'error',
        file: testFile,
        message: `Test class '${className}' should end with 'Tests'`,
        suggestion: `Rename to '${className}Tests'`,
        autoFixable: true,
      });
    }
  }

  // Validate test method naming
  const methodMatches = content.matchAll(/\[(Fact|Theory)\]\s*\n\s*public (?:async Task|void) (\w+)\(/g);

  for (const match of methodMatches) {
    const methodName = match[2];

    // Check if follows convention
    const isValidName = PATTERNS.testMethodNaming.test(methodName) ||
                        PATTERNS.testMethodNamingAlt.test(methodName);

    if (!isValidName) {
      // Check for common patterns that could be improved
      if (methodName.includes('Test') && !methodName.includes('_')) {
        result.violations.push({
          type: 'naming',
          severity: 'warning',
          file: testFile,
          message: `Test method '${methodName}' doesn't follow naming convention`,
          suggestion: `Rename to format: {Method}_When{Condition}_Should{Result} (e.g., GetById_WhenExists_ShouldReturnEntity)`,
          autoFixable: false,
        });
      } else if (!methodName.includes('_')) {
        result.violations.push({
          type: 'naming',
          severity: 'warning',
          file: testFile,
          message: `Test method '${methodName}' should use underscores to separate parts`,
          suggestion: `Use format: {Method}_When{Condition}_Should{Result}`,
          autoFixable: false,
        });
      }
    }
  }
}

function validatePatterns(
  testFile: string,
  content: string,
  result: TestConventionResult
): void {
  // Check for AAA pattern usage
  const methodBlocks = content.matchAll(/\[(Fact|Theory)\][^\[]*?public (?:async Task|void) \w+\([^)]*\)\s*\{([^}]+)\}/gs);

  for (const match of methodBlocks) {
    const methodBody = match[2];

    const hasArrange = PATTERNS.arrangeComment.test(methodBody);
    const hasAct = PATTERNS.actComment.test(methodBody);
    const hasAssert = PATTERNS.assertComment.test(methodBody);

    // Only flag if none of the AAA comments are present and method is non-trivial
    if (!hasArrange && !hasAct && !hasAssert && methodBody.split('\n').length > 3) {
      result.violations.push({
        type: 'pattern',
        severity: 'warning',
        file: testFile,
        message: 'Test method missing AAA (Arrange-Act-Assert) comments',
        suggestion: 'Add // Arrange, // Act, // Assert comments to improve readability',
        autoFixable: false,
      });
      break; // Only report once per file
    }
  }

  // Check for proper async/await usage
  const asyncMethods = content.matchAll(/public async Task (\w+)\([^)]*\)\s*\{([^}]+)\}/gs);
  for (const match of asyncMethods) {
    const methodName = match[1];
    const methodBody = match[2];

    if (!methodBody.includes('await')) {
      result.violations.push({
        type: 'pattern',
        severity: 'warning',
        file: testFile,
        message: `Async test method '${methodName}' doesn't contain 'await'`,
        suggestion: 'Either add await or change to synchronous void method',
        autoFixable: false,
      });
    }
  }
}

function validateAssertions(
  testFile: string,
  content: string,
  result: TestConventionResult
): void {
  // Check for FluentAssertions usage
  const hasFluentAssertions = PATTERNS.fluentAssertions.test(content);
  const hasXunitAssert = /Assert\.\w+\(/.test(content);

  if (hasXunitAssert && !hasFluentAssertions) {
    result.violations.push({
      type: 'assertion',
      severity: 'warning',
      file: testFile,
      message: 'Using xUnit Assert instead of FluentAssertions',
      suggestion: 'Replace Assert.Equal(expected, actual) with actual.Should().Be(expected)',
      autoFixable: false,
    });
  }

  // Check for common FluentAssertions patterns
  if (hasFluentAssertions) {
    // Check for proper null assertions
    if (content.includes('.Should().NotBe(null)')) {
      result.violations.push({
        type: 'assertion',
        severity: 'warning',
        file: testFile,
        message: 'Use .Should().NotBeNull() instead of .Should().NotBe(null)',
        suggestion: 'Replace .Should().NotBe(null) with .Should().NotBeNull()',
        autoFixable: true,
      });
    }

    // Check for proper exception assertions
    if (content.includes('.Should().Throw<') && content.includes('async')) {
      if (!content.includes('.Should().ThrowAsync<')) {
        result.suggestions.push('Use .Should().ThrowAsync<T>() for async exception testing');
      }
    }
  }

  // Check for assertion presence in test methods
  const methodBlocks = content.matchAll(/\[(Fact|Theory)\][^\[]*?public (?:async Task|void) (\w+)\([^)]*\)\s*\{([^}]+)\}/gs);
  for (const match of methodBlocks) {
    const methodName = match[2];
    const methodBody = match[3];

    const hasAssertion = methodBody.includes('.Should()') ||
                        methodBody.includes('Assert.') ||
                        methodBody.includes('Verify(') ||
                        methodBody.includes('VerifyAll()');

    if (!hasAssertion) {
      result.violations.push({
        type: 'assertion',
        severity: 'error',
        file: testFile,
        message: `Test method '${methodName}' has no assertions`,
        suggestion: 'Add at least one assertion to verify expected behavior',
        autoFixable: false,
      });
    }
  }
}

function validateMocking(
  testFile: string,
  content: string,
  result: TestConventionResult
): void {
  const hasMoq = PATTERNS.moqUsage.test(content);

  if (hasMoq) {
    // Check for proper Moq setup
    if (content.includes('new Mock<') && !content.includes('.Setup(')) {
      result.violations.push({
        type: 'mocking',
        severity: 'warning',
        file: testFile,
        message: 'Mock objects created but no Setup() calls found',
        suggestion: 'Add .Setup() calls to define mock behavior',
        autoFixable: false,
      });
    }

    // Check for Verify usage
    const hasSetup = content.includes('.Setup(');
    const hasVerify = content.includes('.Verify(') || content.includes('.VerifyAll()');

    if (hasSetup && !hasVerify) {
      result.suggestions.push(`Consider adding .Verify() calls in ${testFile} to verify mock interactions`);
    }

    // Check for strict vs loose mocking
    if (content.includes('MockBehavior.Strict')) {
      result.suggestions.push('Using MockBehavior.Strict - ensure all mock calls are set up');
    }
  }

  // Check for service mocking in controller tests
  if (testFile.includes('Controller')) {
    if (!hasMoq && !content.includes('WebApplicationFactory')) {
      result.violations.push({
        type: 'mocking',
        severity: 'warning',
        file: testFile,
        message: 'Controller tests should use mocking or WebApplicationFactory',
        suggestion: 'Use Moq for unit tests or WebApplicationFactory for integration tests',
        autoFixable: false,
      });
    }
  }
}

// ============================================================================
// Formatting
// ============================================================================

function formatValidationResult(result: TestConventionResult): string {
  const lines: string[] = [];

  lines.push('# Test Convention Validation');
  lines.push('');

  // Summary
  const status = result.valid ? '✅ PASSED' : '❌ FAILED';
  const errorCount = result.violations.filter(v => v.severity === 'error').length;
  const warningCount = result.violations.filter(v => v.severity === 'warning').length;

  lines.push('## Summary');
  lines.push('');
  lines.push(`| Status | ${status} |`);
  lines.push('|--------|----------|');
  lines.push(`| Errors | ${errorCount} |`);
  lines.push(`| Warnings | ${warningCount} |`);
  if (result.autoFixedCount > 0) {
    lines.push(`| Auto-fixed | ${result.autoFixedCount} |`);
  }
  lines.push('');

  // Violations
  if (result.violations.length > 0) {
    lines.push('## Violations');
    lines.push('');

    // Group by type
    const byType = new Map<string, TestConventionViolation[]>();
    for (const v of result.violations) {
      const existing = byType.get(v.type) || [];
      existing.push(v);
      byType.set(v.type, existing);
    }

    for (const [type, violations] of byType) {
      lines.push(`### ${type.charAt(0).toUpperCase() + type.slice(1)} Violations`);
      lines.push('');

      for (const v of violations) {
        const emoji = v.severity === 'error' ? '❌' : '⚠️';
        lines.push(`${emoji} **${v.file}**`);
        lines.push(`  - ${v.message}`);
        lines.push(`  - 💡 ${v.suggestion}`);
        if (v.autoFixable) {
          lines.push(`  - 🔧 Auto-fixable`);
        }
        lines.push('');
      }
    }
  }

  // Suggestions
  if (result.suggestions.length > 0) {
    lines.push('## Suggestions');
    lines.push('');
    for (const suggestion of result.suggestions) {
      lines.push(`💡 ${suggestion}`);
      lines.push('');
    }
  }

  // Convention reference
  lines.push('## SmartStack Test Conventions');
  lines.push('');
  lines.push('### Naming');
  lines.push('- Files: `{ComponentName}Tests.cs`');
  lines.push('- Classes: `{ComponentName}Tests`');
  lines.push('- Methods: `{MethodName}_When{Condition}_Should{ExpectedResult}`');
  lines.push('');
  lines.push('### Structure');
  lines.push('```');
  lines.push('Tests/');
  lines.push('├── Unit/');
  lines.push('│   ├── Domain/');
  lines.push('│   ├── Services/');
  lines.push('│   └── Validators/');
  lines.push('├── Integration/');
  lines.push('│   ├── Controllers/');
  lines.push('│   └── Repositories/');
  lines.push('└── Security/');
  lines.push('```');
  lines.push('');
  lines.push('### Pattern (AAA)');
  lines.push('```csharp');
  lines.push('[Fact]');
  lines.push('public async Task GetById_WhenExists_ShouldReturnEntity()');
  lines.push('{');
  lines.push('    // Arrange');
  lines.push('    var id = Guid.NewGuid();');
  lines.push('');
  lines.push('    // Act');
  lines.push('    var result = await _sut.GetByIdAsync(id);');
  lines.push('');
  lines.push('    // Assert');
  lines.push('    result.Should().NotBeNull();');
  lines.push('}');
  lines.push('```');
  lines.push('');

  return lines.join('\n');
}
